import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Clock,
  Users,
  Search,
  SlidersHorizontal,
  GraduationCap,
  Award,
} from "lucide-react";
import { eCourseService } from "../../services/elearning/eCourseService";
import StarRating from "../../components/common/StarRating";

// Updated interface to match backend data structure
interface Course {
  _id: string;
  title: string;
  description: string;
  shortDescription?: string;
  instructor: {
    _id: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  category: string;
  level: "Beginner" | "Intermediate" | "Advanced";
  duration: {
    estimatedHours: number;
    estimatedMinutes: number;
  };
  pricing: {
    type: "free" | "paid";
    amount: number;
    currency: string;
  };
  registration: {
    type: "self" | "manual" | "invite-only";
    requiresApproval: boolean;
    maxStudents?: number;
  };
  status: "draft" | "published" | "archived" | "suspended";
  visibility: "public" | "private" | "branch-only";
  thumbnail?: string;
  language: string;
  createdAt: string;
  updatedAt: string;
  // Additional computed fields
  totalStudents?: number;
  rating?: {
    average: number;
    count: number;
  };
  isEnrolled?: boolean;
  progress?: number;
  chain?: {
    chainId?: string;
    sequenceNumber?: number;
    isChainFinal?: boolean;
    nextCourseId?: string;
  };
}

const CourseCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [sortBy, setSortBy] = useState("popular");
  const [showFilters, setShowFilters] = useState(false);

  // Fetch courses data
  const { data: coursesData, isLoading } = useQuery({
    queryKey: ["public-courses"],
    queryFn: () => eCourseService.getPublicCourses(),
  });

  const courses = coursesData?.data?.courses || [];

  // Categories from backend ECourse model
  const categories = [
    "all",
    "Programming",
    "Design",
    "Business",
    "Marketing",
    "Language",
    "Science",
    "Mathematics",
    "Arts",
    "Health",
    "Technology",
    "Other",
  ];

  const levels = ["all", "Beginner", "Intermediate", "Advanced"];
  const sortOptions = [
    { value: "popular", label: "Most Popular" },
    { value: "rating", label: "Highest Rated" },
    { value: "newest", label: "Newest" },
    { value: "alphabetical", label: "Alphabetical" },
    { value: "duration", label: "Duration" },
  ];

  const handleCourseClick = (courseId: string) => {
    navigate(`/elearning/course/${courseId}`);
  };

  const handleEnroll = async (courseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // For teacher view, enrollment doesn't make sense
      // This might be navigation to course management instead
      console.log("Managing course:", courseId);
      navigate(`/elearning/course/${courseId}`);
    } catch (error) {
      console.error("Error navigating to course:", error);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "beginner":
        return "bg-green-100 text-green-800";
      case "intermediate":
        return "bg-yellow-100 text-yellow-800";
      case "advanced":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg p-6">
                  <div className="h-48 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BookOpen className="h-8 w-8 mr-3 text-blue-600" />
                Course Catalog
              </h1>
              <p className="mt-1 text-gray-600">
                Discover and enroll in courses to advance your learning
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <SlidersHorizontal className="h-5 w-5 mr-2" />
                Filters
              </button>

              {/* Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category === "all" ? "All Categories" : category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Level Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Level
                    </label>
                    <select
                      value={selectedLevel}
                      onChange={(e) => setSelectedLevel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {levels.map((level) => (
                        <option key={level} value={level}>
                          {level === "all"
                            ? "All Levels"
                            : level.charAt(0).toUpperCase() + level.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Course Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses
            .filter((course: Course) => {
              const matchesSearch = course.title
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
              const matchesCategory =
                selectedCategory === "all" ||
                course.category === selectedCategory;
              const matchesLevel =
                selectedLevel === "all" || course.level === selectedLevel;
              return matchesSearch && matchesCategory && matchesLevel;
            })
            .sort((a: Course, b: Course) => {
              switch (sortBy) {
                case "rating":
                  return (b.rating?.average || 0) - (a.rating?.average || 0);
                case "newest":
                  return (
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                  );
                case "alphabetical":
                  return a.title.localeCompare(b.title);
                case "duration":
                  return (
                    (b.duration?.estimatedHours || 0) -
                    (a.duration?.estimatedHours || 0)
                  );
                default:
                  return 0;
              }
            })
            .map((course: Course) => (
              <div
                key={course._id}
                onClick={() => handleCourseClick(course._id)}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer overflow-hidden"
              >
                {/* Course Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-blue-500 to-purple-600">
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to gradient if image fails to load
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  {course.isEnrolled && (
                    <div className="absolute top-3 right-3">
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                        Enrolled
                      </span>
                    </div>
                  )}
                  {course.chain?.chainId && (
                    <div className="absolute top-3 right-3">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center ${
                          course.chain.isChainFinal
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        <Award className="w-3 h-3 mr-1" />
                        Part {course.chain.sequenceNumber}
                        {course.chain.isChainFinal && " (Final)"}
                      </span>
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${getLevelColor(
                        course.level
                      )}`}
                    >
                      {course.level.charAt(0).toUpperCase() +
                        course.level.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Course Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                      {course.title}
                    </h3>
                    <span className="text-sm text-gray-500 ml-2">
                      {course._id.substring(0, 8).toUpperCase()}
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {course.description}
                  </p>

                  <div className="flex items-center mb-3">
                    <GraduationCap className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-600">
                      {course.instructor.firstName} {course.instructor.lastName}
                    </span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center justify-between mb-3">
                    <StarRating
                      rating={course.rating?.average || 0}
                      size="sm"
                      readonly={true}
                      showValue={true}
                    />
                    <span className="text-sm text-gray-500">
                      ({course.rating?.count || 0} reviews)
                    </span>
                  </div>

                  {/* Course Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {course.duration.estimatedHours} hours
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {course.registration.maxStudents
                        ? `${course.registration.maxStudents} max`
                        : "No limit"}
                    </div>
                  </div>

                  {/* Progress Bar (if enrolled) */}
                  {course.isEnrolled && course.progress !== undefined && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{course.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${course.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                      {course.category}
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                      {course.level}
                    </span>
                    {course.chain?.chainId && (
                      <span
                        className={`text-xs px-2 py-1 rounded-full flex items-center ${
                          course.chain.isChainFinal
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        <Award className="w-3 h-3 mr-1" />
                        Series Course
                        {course.chain.isChainFinal && " • Certificate"}
                      </span>
                    )}
                  </div>

                  {/* Action Button */}
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-semibold text-gray-900">
                      {course.pricing.type === "free"
                        ? "Free"
                        : `KES ${course.pricing.amount.toFixed(2)}`}
                    </div>
                    {!course.isEnrolled ? (
                      <button
                        onClick={(e) => handleEnroll(course._id, e)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Enroll Now
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/elearning/course/${course._id}`);
                        }}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Continue
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Empty State */}
        {courses.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">
              No courses found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseCatalog;
